/**
 * Lockdown utilities for Secure Exam Mode.
 * Provides hooks for fullscreen enforcement, tab-switch detection,
 * and blocking right-click / copy / paste on the exam container.
 */
import { useEffect, useRef, useCallback } from "react";

export function useLockdown({
  enabled,
  onTabSwitch,
  onFullscreenExit,
  onCopyAttempt,
  onRightClick,
  onKeyboardBlock
}) {
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  // Tab / window switch detection
  useEffect(() => {
    if (!enabled) return;
    const handleVis = () => {
      if (document.visibilityState === "hidden" && enabledRef.current) {
        onTabSwitch?.({ at: new Date().toISOString() });
      }
    };
    const handleBlur = () => {
      if (enabledRef.current) onTabSwitch?.({ at: new Date().toISOString(), cause: "blur" });
    };
    document.addEventListener("visibilitychange", handleVis);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVis);
      window.removeEventListener("blur", handleBlur);
    };
  }, [enabled, onTabSwitch]);

  // Fullscreen exit detection
  useEffect(() => {
    if (!enabled) return;
    const handleFsChange = () => {
      if (!document.fullscreenElement && enabledRef.current) {
        onFullscreenExit?.();
      }
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, [enabled, onFullscreenExit]);

  // Right-click, copy, paste, selection, devtools shortcuts
  useEffect(() => {
    if (!enabled) return;
    const handleContext = (e) => { e.preventDefault(); onRightClick?.(); };
    const handleCopy = (e) => { e.preventDefault(); onCopyAttempt?.(); };
    const handlePaste = (e) => { e.preventDefault(); };
    const handleCut = (e) => { e.preventDefault(); onCopyAttempt?.(); };
    const handleKey = (e) => {
      // Block: F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U, Ctrl+S, Ctrl+P
      const k = e.key?.toLowerCase();
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (k === "i" || k === "j" || k === "c")) ||
        (e.ctrlKey && (k === "u" || k === "s" || k === "p"))
      ) {
        e.preventDefault();
        onKeyboardBlock?.(e.key);
      }
    };
    document.addEventListener("contextmenu", handleContext);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("paste", handlePaste);
    document.addEventListener("cut", handleCut);
    document.addEventListener("keydown", handleKey);
    // Disable text selection style
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
    return () => {
      document.removeEventListener("contextmenu", handleContext);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("cut", handleCut);
      document.removeEventListener("keydown", handleKey);
      document.body.style.userSelect = prev;
    };
  }, [enabled, onRightClick, onCopyAttempt, onKeyboardBlock]);

  const enterFullscreen = useCallback(async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    } catch (e) {
      // Silent — caller can prompt user
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
    } catch (e) { /* ignore */ }
  }, []);

  return { enterFullscreen, exitFullscreen };
}
